import {
  format,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameYear,
  getWeekOfMonth,
} from "date-fns";
import { vi } from "date-fns/locale";

import { Calendar, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Icon } from "@iconify/react";

import { Button } from "../../ui/button";
import Can from "../../../auth/Can";
import { Perm } from "../../../auth/permissions";
import { ImageWithFallback } from "../../landing/ImageWithFallback";

export type ScheduleViewMode = "day" | "week";

interface ScheduleNavBarProps {
  currentDate: Date;
  viewMode?: ScheduleViewMode;
  onDateChange: (date: Date) => void;
  onViewModeChange: (mode: ScheduleViewMode) => void;
  onAddSlot: () => void;
}

export default function ScheduleNavBar({
  currentDate,
  viewMode = "week",
  onDateChange,
  onViewModeChange,
  onAddSlot,
}: ScheduleNavBarProps) {
  const handlePrevious = () => {
    if (viewMode === "day") {
      onDateChange(subDays(currentDate, 1));
    } else {
      onDateChange(subDays(currentDate, 7));
    }
  };

  const handleNext = () => {
    if (viewMode === "day") {
      onDateChange(addDays(currentDate, 1));
    } else {
      onDateChange(addDays(currentDate, 7));
    }
  };

  const handleToday = () => {
    onDateChange(new Date());
  };

  const getDateRangeText = () => {
    if (viewMode === "day") {
      // "Thứ Ba, 04/11/2025"
      return format(currentDate, "EEEE, dd/MM/yyyy", { locale: vi });
    }
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1, locale: vi });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1, locale: vi });

    // Tuần trong THÁNG (1-based) của currentDate, pad thành "01", "02", ...
    const weekNoInMonth = String(
      getWeekOfMonth(currentDate, { weekStartsOn: 1, locale: vi })
    ).padStart(2, "0");
    const weekLabel = `Tuần ${weekNoInMonth}`;

    // Khoảng ngày cho các trường hợp vắt tháng/năm
    let range: string;
    if (isSameMonth(weekStart, weekEnd) && isSameYear(weekStart, weekEnd)) {
      // 03–09/11
      range = `${format(weekStart, "dd", { locale: vi })}–${format(
        weekEnd,
        "dd/MM/yyyy",
        { locale: vi }
      )}`;
    } else if (isSameYear(weekStart, weekEnd)) {
      // 28/10–03/11
      range = `${format(weekStart, "dd/MM", { locale: vi })}–${format(
        weekEnd,
        "dd/MM/yyyy",
        { locale: vi }
      )}`;
    } else {
      // 30/12–05/01
      range = `${format(weekStart, "dd/MM/yyyy", { locale: vi })}–${format(
        weekEnd,
        "dd/MM/yyyy",
        { locale: vi }
      )}`;
    }

    // Kết quả: "Tuần 02, 03–09/11"
    return `${weekLabel}, ${range}`;
  };

  return (
    <div className="sticky top-0 z-10 border-b-2 border-green-600 bg-background px-3 sm:px-4 lg:px-6">
      <div className="h-auto sm:h-16 py-2 sm:py-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        {/* Left: Title */}
        <div className="flex items-center justify-between sm:justify-start gap-3">
          <div className="flex items-center gap-2 min-w-0 shrink-0">
            <ImageWithFallback src="/icons/calendar.svg" className="w-10 h-10"/>
            <h1
              className="text-lg sm:text-lg font-semibold truncate"
              data-testid="text-app-title"
              title="Thiết lập lịch khám"
            >
              Thiết lập lịch khám
            </h1>
          </div>

          <Can perm={Perm.Schedule_Manage}>
            <Button
              onClick={onAddSlot}
              data-testid="button-add-slot"
              className="md:hidden block px-3 py-0 bg-gradient-to-r from-primary to-primary-glow ml-auto sm:ml-0"
            >
              <Plus className="h-auto w-3 sm:mr-2" />
              <span className="hidden sm:inline">Thêm lịch khám</span>
            </Button>
          </Can>
        </div>

        {/* Right: Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full justify-center">
          {/* Date navigation */}
          <div className="flex items-center gap-2 w-full sm:w-auto border border-gray-400 rounded">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePrevious}
              data-testid="button-previous"
              className="shrink-0 border-transparent rounded-r-none border-r border-r-gray-400"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div
              className="text-sm font-medium flex-1 sm:flex-none sm:min-w-[260px] lg:min-w-[280px] text-center truncate px-1"
              data-testid="text-date-range"
              title={getDateRangeText()}
            >
              {getDateRangeText()}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={handleNext}
              data-testid="button-next"
              className="shrink-0 border-transparent rounded-l-none border-l border-l-gray-400"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Today + View mode + Add */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToday}
              data-testid="button-today"
              className="shrink-0"
            >
              <span className="inline">Hôm nay</span>
            </Button>

            {/* <div className="flex items-center gap-1 border rounded-md p-1 shrink-0">
              <Button
                variant={viewMode === "day" ? "default" : "ghost"}
                size="sm"
                onClick={() => onViewModeChange("day")}
                data-testid="button-view-day"
                className={[
                  "h-8 px-3 sm:px-4",
                  viewMode === "day" &&
                    "bg-gradient-to-r from-primary to-primary-glow",
                ].join(" ")}
              >
                Ngày
              </Button>

              <Button
                variant={viewMode === "week" ? "default" : "ghost"}
                size="sm"
                onClick={() => onViewModeChange("week")}
                data-testid="button-view-week"
                className={[
                  "h-8 px-3 sm:px-4",
                  viewMode === "week" &&
                    "bg-gradient-to-r from-primary to-primary-glow",
                ].join(" ")}
              >
                Tuần
              </Button>
            </div> */}
          </div>
        </div>

        <Can perm={Perm.Schedule_Manage}>
          <Button
            onClick={onAddSlot}
            data-testid="button-add-slot"
            className="md:flex hidden bg-gradient-to-r from-primary to-primary-glow ml-auto sm:ml-0"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Thêm lịch khám</span>
          </Button>
        </Can>
      </div>
    </div>
  );
}
